import type { Session } from '@toolpad/core';
import { AppProvider, Router } from '@toolpad/core/AppProvider';
import { DashboardLayout } from "@toolpad/core/DashboardLayout";

import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';

import theme from '../theme';
import { DataLoaderView } from "./dataloader";
import { MenuNavigation } from './menunavigation';
import { Group } from '../model/group';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/store';
import { Program } from '../model/program';
import { getApiAccessToken } from '../auth/auth';
import { Course } from '../model/course';
import { getUserRole, UserRole } from '../auth/jwt';
import { setCurrentProgram } from '../reducers/programSlice';

export const NavigationView: React.FC = () => {
    const dispatch = useDispatch();
    
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const [accessToken, setAccessToken] = useState<string | null>(null);

    const programs: Program[] = useSelector((state: RootState) => state.programs.programs);
    const currentProgram: Program | null = useSelector((state: RootState) => state.programs.currentProgram);

    // pathName is used for navigation within the app
    // we parse the path to determine what to show in the MainView
    // for example: `/modules/2b61c7aa-2eb0-420a-9664-01dfe0410799`
    
    const [pathname, setPathname] = React.useState(() => {
      // On initial load, if user is client admin, go to /programs, else /clients

      const userRole = getUserRole(activeAccount);

      if (userRole == UserRole.CLIENT_ADMIN) {
        if (currentProgram) {
          return `/programs/${currentProgram.id}`;
        }

        // in case no program uuid is defined
        if (programs.length > 0) {
          dispatch(setCurrentProgram(programs[0]));
          return `/programs/${programs[0].id}`;
        } else {
          console.log("no programs available")
          return `/programs`
        }
      }
      
      else if (userRole == UserRole.ADMIN) {
        return '/clients';
      } else {
        return '/'
      }
    });

    const currentGroup: Group | null = useSelector((state: RootState) => state.groups.currentGroup);
    const courses: Course[] = useSelector((state: RootState) => state.courses.courses);

    const router = React.useMemo<Router>(() => {
        return {
            pathname,
            searchParams: new URLSearchParams(),
            navigate: (path) => {
              setPathname(String(path))
            },
        };
    }, [pathname]);

    const handleLogoutRedirect = () => {
        // Clear browser cache
        localStorage.clear();
        sessionStorage.clear();

        // Clear redux cache
        dispatch({ type: 'LOGOUT' });

        instance.logoutRedirect().catch((error) => console.log(error));
    };

    const [session, setSession] = React.useState<Session | null>({
      user: {
        email: activeAccount?.username,
      },
    });

    const authentication =  React.useMemo(() => {      
      getApiAccessToken(instance, activeAccount).then((token) => {
        setAccessToken(token);
      });
      
      return {
        signIn: () => {
          setSession({
            user: {
              email: activeAccount?.username,
            },
          });
        },
        signOut: () => {
          setSession(null);
          handleLogoutRedirect()
        },
      };
    }, []);

    return (
        <div className="approot">
            <div className="app">
                <AppProvider
                    navigation={
                      MenuNavigation({
                        accessToken: accessToken,
                        programs: programs,
                        courses: courses
                      })
                    }
                    branding={{
                        logo: <img src="/logo800.png" alt="Pacy logo"/>,
                        title: currentGroup ? currentGroup.displayName : '',
                    }}
                    session={session}
                    authentication={authentication}
                    router={router}
                    theme={theme}
                    >
                    <DashboardLayout>
                      <DataLoaderView target={pathname}/>
                    </DashboardLayout>
                </AppProvider>
            </div>
        </div>
    )
}